import React from 'react';

import { useClassnames } from '@lumapps/classnames';
import { CheckEmailsPage } from '@lumapps/login/components/CheckEmailsPage';
import { LoginPage } from '@lumapps/login/components/LoginPage';
import { useSentLinkMessage } from '@lumapps/login/hooks/useSentLinkMessage';
import { StepProps } from '@lumapps/lumx-layouts/types';
import { mdiArrowLeft } from '@lumapps/lumx/icons';
import { Button, Emphasis } from '@lumapps/lumx/react';
import { useLocation, useRouter } from '@lumapps/router';
import { GLOBAL, useTranslate } from '@lumapps/translations';

import { SIGN_IN } from '../../keys';
import { signIn, STEPS } from '../../routes';

/** Block of the page wrapper, whose stylesheet positions the back button. */
const LOGIN_PAGE_CLASSNAME = 'login-page';

/**
 * Landing page of the email two factor authentication flow.
 *
 * Renders the same screen as the magic link flow, with two differences that both come from the page
 * being reachable through a full page load: the backend redirects here once the password has been
 * validated and the sign-in link has been sent, so there is no wizard state.
 *
 * - The address the link was sent to is unknown, so the sentence falls back to a generic stand-in.
 * - Going back targets the sign-in step explicitly rather than through `goToPreviousStep`, which is
 *   the previous step of the wizard array and would land on the magic link flow.
 */
export const CheckEmailsTwoFactorStep: React.FC<StepProps> = ({ state, ...props }) => {
    const { translateKey } = useTranslate();
    const { redirect } = useRouter();
    const { element } = useClassnames(LOGIN_PAGE_CLASSNAME);
    const { search } = useLocation();
    const message = useSentLinkMessage(state?.email, { withGenericFallback: true });

    const goToSignIn = React.useCallback(() => {
        redirect(signIn({ step: STEPS.SIGN_IN }, search));
    }, [redirect, search]);

    return (
        <LoginPage
            title={translateKey(SIGN_IN.CHECK_YOUR_EMAILS)}
            before={
                <Button
                    className={element('back-button')}
                    leftIcon={mdiArrowLeft}
                    emphasis={Emphasis.low}
                    onClick={goToSignIn}
                >
                    {translateKey(GLOBAL.BACK)}
                </Button>
            }
            displayPoweredBy={false}
        >
            <CheckEmailsPage {...props} state={state} message={message} hasTryAnotherAddress={false} />
        </LoginPage>
    );
};
